import { forwardRef, useId } from 'react';
import type { ReactNode, SelectHTMLAttributes } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from './utils/cn';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  options: SelectOption[];
  placeholder?: string;
  containerClassName?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      hint,
      error,
      options,
      placeholder,
      id,
      className,
      containerClassName,
      required,
      ...rest
    },
    ref,
  ) => {
    const reactId = useId();
    const selectId = id ?? reactId;
    const describedById = error ? `${selectId}-err` : hint ? `${selectId}-hint` : undefined;

    return (
      <div className={cn('flex flex-col gap-1.5', containerClassName)}>
        {label && (
          <label htmlFor={selectId} className="text-sm font-medium text-fg">
            {label}
            {required ? <span className="text-danger ms-0.5">*</span> : null}
          </label>
        )}
        <div
          className={cn(
            'relative flex items-center h-11 rounded-md',
            'bg-surface border border-border',
            'transition-colors duration-150',
            'focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/30',
            error && 'border-danger focus-within:border-danger focus-within:ring-danger/30',
          )}
        >
          <select
            ref={ref}
            id={selectId}
            aria-invalid={!!error}
            aria-describedby={describedById}
            required={required}
            className={cn(
              'appearance-none flex-1 min-w-0 ps-3 pe-9 h-full bg-transparent outline-none text-sm',
              'disabled:cursor-not-allowed disabled:opacity-60',
              className,
            )}
            {...rest}
          >
            {placeholder ? (
              <option value="" disabled>
                {placeholder}
              </option>
            ) : null}
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <ChevronDown
            className="absolute end-3 h-4 w-4 text-fg-subtle pointer-events-none"
            aria-hidden
          />
        </div>
        {error ? (
          <p id={`${selectId}-err`} className="text-xs text-danger">
            {error}
          </p>
        ) : hint ? (
          <p id={`${selectId}-hint`} className="text-xs text-fg-subtle">
            {hint}
          </p>
        ) : null}
      </div>
    );
  },
);
Select.displayName = 'Select';
